import type { OfficeDb } from './db.js'
import { CURSORS_KEY, getMeta, setMeta, PRIMARY_KEY } from './db.js'
import { reconcile, backoffMs, type Mutation, type PushResult } from './outbox.js'
import { computeSkew } from './clock.js'

export interface SyncTransport {
  push(deviceId: string, mutations: Mutation[]): Promise<{ results: PushResult[] }>
  pull(cursors: Record<string, string | null>): Promise<{
    tables: Record<string, { table: string; rows: unknown[]; hasMore: boolean; nextCursor: string | null }>
    incomplete: boolean
    serverTime: string
  }>
  time(): Promise<{ epochMs: number }>
}

export interface SyncOptions {
  db: OfficeDb
  transport: SyncTransport
  deviceId: string
  deviceNow?: () => number
  /** Batch size per push. Keeps a huge backlog from timing out in one request. */
  batchSize?: number
  /** Safety valve on pull pagination loops. */
  maxPullRounds?: number
}

export interface SyncOutcome {
  pushed: number
  rejected: number
  pulled: number
  rounds: number
  /** Pulled rows the local store could not key, and therefore could not save. */
  skipped: number
  skewMs: number | null
  error: string | null
}

/** Tables the server is allowed to write into the local mirror. */
const MIRRORED: ReadonlySet<string> = new Set([
  'people', 'members', 'guests', 'face_templates', 'presence_events', 'visits',
  'chore_definitions', 'chore_instances', 'chore_swaps',
  'proposal_types', 'proposals', 'proposal_votes',
  'resources', 'bookings', 'occupancy', 'queue_entries', 'matches', 'ratings',
  'bills', 'bill_instances', 'bill_payments', 'expenses', 'settlements', 'food_orders',
  'supply_items', 'supply_events', 'notes', 'tasks', 'wiki_pages',
  'checklists', 'checklist_runs', 'space_mode', 'ramadan_periods', 'settings',
  'floor_plans', 'room_labels',
])

/**
 * Tables whose pulled rows can arrive as a TOMBSTONE, and the column that marks
 * one.
 *
 * A deletion cannot travel as an absence: the server only sends rows that
 * exist, so a hard-deleted note simply stopped appearing and every device that
 * already had it kept it forever. It travels as a row carrying a marker
 * instead, and that row means DELETE THE LOCAL COPY, not "store this".
 *
 * A map rather than a hardcoded `notes`/`deleted_at` because the next
 * soft-deleted table should be one line here; a map rather than "any row with a
 * `deleted_at`" because that reads a deletion into any table that happens to
 * grow a column by that name. Nothing here widens what the server may touch:
 * this is consulted INSIDE the `MIRRORED` allow-list, so a page named `meta` or
 * `outbox` is still discarded before any of it runs — deleting rows on the
 * server's say-so is the direction where that guard matters most.
 */
const TOMBSTONE_COLUMN: Record<string, string> = {
  notes: 'deleted_at',
}

/** One component of a primary key, or null if it is not a usable key value. */
const keyPart = (v: unknown): string | number | null => {
  if (typeof v === 'number') return v
  return typeof v === 'string' && v !== '' ? v : null
}

/**
 * The primary key of a row AS DEXIE WANTS IT, or null if the row carries none.
 *
 * `String(undefined)` is `'undefined'`, so this MUST test the values rather than
 * stringify them: a check that cannot fail is not a check. A row with no key is
 * one IndexedDB will refuse, and `bulkPut` rejecting mid-round used to abort the
 * whole pull — every table, every cursor, every round, forever, because the
 * server keeps sending the same bad row. One unstorable row must cost that row,
 * not the sync.
 *
 * A compound key comes back as an ARRAY and not as a joined string, because
 * this value is now also handed to `bulkDelete`, and `'a|b'` deletes nothing
 * from a table keyed on `[proposal_id+voter_id]` — silently, which is how a
 * tombstone would go missing without anything failing.
 */
type RowKey = string | number | (string | number)[]

const rowKey = (table: string, row: Record<string, unknown>): RowKey | null => {
  const pk = PRIMARY_KEY[table] ?? 'id'
  if (!Array.isArray(pk)) return keyPart(row[pk])
  const values: (string | number)[] = []
  for (const k of pk) {
    const v = keyPart(row[k])
    if (v === null) return null
    values.push(v)
  }
  return values
}

/**
 * One sync cycle: push the outbox, then pull everything new.
 *
 * PULL LOOPS UNTIL COMPLETE (docs/02 §8, "Landmine 1"). A page that comes back
 * `hasMore` is not a finished sync. Chore streaks and rotation position are
 * DERIVED from full history, so stopping on a partial read produces silently
 * wrong streaks that never self-correct. `hasMore` is an explicit server signal,
 * never inferred from array length.
 */
export async function syncOnce(opts: SyncOptions): Promise<SyncOutcome> {
  const { db, transport, deviceId } = opts
  const batchSize = opts.batchSize ?? 200
  const maxRounds = opts.maxPullRounds ?? 50
  const deviceNow = opts.deviceNow ?? Date.now
  const out: SyncOutcome = {
    pushed: 0, rejected: 0, pulled: 0, rounds: 0, skipped: 0, skewMs: null, error: null,
  }
  // Everything that went wrong without stopping the cycle. Collected rather than
  // thrown, so one bad table cannot cost the others their page. A SET, because
  // the same complaint can recur on every one of up to `maxPullRounds` rounds
  // and an error message nobody can read is an error message nobody reads.
  const problems = new Set<string>()
  let complete = false

  try {
    // 0. Trust the server's epoch over the device's own.
    const before = deviceNow()
    const t = await transport.time()
    out.skewMs = computeSkew(t.epochMs, before)

    // 1. Push the outbox in batches, oldest first.
    for (;;) {
      const batch = await db.outbox.orderBy('queuedAt').limit(batchSize).toArray()
      if (batch.length === 0) break
      const { results } = await transport.push(deviceId, batch)
      const report = reconcile(batch, results, deviceNow())

      await db.transaction('rw', db.outbox, db.rejections, async () => {
        const settled = [...report.applied, ...report.duplicate, ...report.rejected.map(r => r.mutation.id)]
        await db.outbox.bulkDelete(settled)
        // Rejections are TERMINAL: recorded for the user, never retried.
        if (report.rejected.length) {
          await db.rejections.bulkPut(report.rejected.map(r => ({ ...r, id: r.mutation.id })))
        }
        // Unanswered mutations stay queued with a bumped attempt count.
        if (report.retained.length) await db.outbox.bulkPut(report.retained)
      })

      out.pushed += report.applied.length + report.duplicate.length
      out.rejected += report.rejected.length
      if (report.retained.length > 0) break   // network trouble; stop pushing this cycle
      if (batch.length < batchSize) break
    }

    // 2. Pull until the server says nothing is left.
    let cursors = await getMeta<Record<string, string | null>>(db, CURSORS_KEY, {})
    for (let round = 0; round < maxRounds; round++) {
      const page = await transport.pull(cursors)
      out.rounds++
      let wrote = 0
      const next: Record<string, string | null> = { ...cursors }

      for (const [table, p] of Object.entries(page.tables)) {
        if (p.rows.length === 0) continue
        // ALLOW-LIST, not "whatever the server named". Without this a page named
        // `meta` would overwrite the stored session, and `outbox`/`rejections`
        // are equally reachable — the client's own tables are not the server's
        // to write.
        if (!MIRRORED.has(table)) continue
        const t = (db as unknown as Record<string, {
          bulkPut(rows: unknown[]): Promise<unknown>
          bulkDelete(keys: unknown[]): Promise<unknown>
        }>)[table]
        if (!t) continue

        // Dexie needs the key inline, compound keys included. A row without one
        // is a row IndexedDB will refuse, so it is dropped HERE — loudly, in
        // `out.error` — instead of being handed to `bulkPut` to reject the whole
        // page with. Dropping it is the same terminal verdict a rejected
        // mutation gets on the push side: one bad item, not the whole queue.
        // A keyless TOMBSTONE is dropped by the same test: without a key there
        // is nothing to delete, and guessing is worse than skipping.
        const marker = TOMBSTONE_COLUMN[table]
        const storable: Record<string, unknown>[] = []
        const tombstoned: RowKey[] = []
        let dropped = 0
        for (const r of p.rows) {
          const row = r as Record<string, unknown>
          const key = rowKey(table, row)
          if (key === null) { dropped++; continue }
          if (marker !== undefined && row[marker] != null) tombstoned.push(key)
          else storable.push(row)
        }
        if (dropped > 0) {
          out.skipped += dropped
          problems.add(`${table}: ${dropped} row(s) with no primary key`)
        }
        try {
          if (storable.length) await t.bulkPut(storable)
          // Deletes AFTER puts. Within one page a row appears once, so the two
          // lists cannot overlap today — but if they ever did, the tombstone is
          // the later state (it carries the higher cursor value, which is why it
          // was sent at all), and a resurrected note that nobody can delete
          // again is the bug this whole mechanism exists to remove.
          if (tombstoned.length) await t.bulkDelete(tombstoned)
          // Tombstones count as work done. A round that applied nothing but
          // deletions and reported `pulled: 0` reads as "nothing arrived" on
          // the sync screen — on the very round that took a note off the wall.
          wrote += storable.length + tombstoned.length
          // The cursor moves only for a table that actually WROTE. Advancing past
          // rows still sitting in a failed page is how a gap becomes permanent.
          if (p.nextCursor) next[table] = p.nextCursor
        } catch (e) {
          // One table's local storage failing must not cost every other table
          // its page and its cursor — that is head-of-line blocking on the read
          // side, and it never self-corrects.
          problems.add(`${table}: ${(e as Error).message}`)
        }
      }
      out.pulled += wrote
      cursors = next
      await setMeta(db, CURSORS_KEY, cursors)
      if (!page.incomplete) { complete = true; break }
    }

    // A pull that ran out of rounds while the server still said "there is more"
    // has NOT synced, and must never be reported as though it had. Landmine 1 is
    // exactly this: a quietly incomplete read produces wrong streaks that never
    // self-correct, and the only thing worse than the sync failing is the sync
    // claiming it worked.
    if (!complete) {
      problems.add(`pull still incomplete after ${maxRounds} rounds`)
    }
    if (problems.size) out.error = [...problems].join('; ')
  } catch (e) {
    out.error = (e as Error).message
  }
  return out
}

export { backoffMs }
